<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { Activity, Partner, UpdateActivityDto } from '@/api-swagger'
import Modal from '@/components/common/layout/Modal.vue'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { useActivitiesStore } from '@/stores/useActivityStore'
import { VQuillEditor } from '@morpheme/quill-editor'
import { DatePicker } from 'v-calendar'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import PartnerButtonRedirect from '@/components/common/buttons/PartnerButtonRedirect.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import PartnerAction from '@/components/features/partners/PartnerAction.vue'
import { usePartnerStore } from '@/stores/usePartnerStore'
import DeleteConfirmation from '@/components/common/buttons/DeleteConfirmation.vue'

const schema = object({
  content: string().required().label('Content'),
})

const { values } = useForm({
  validationSchema: schema,
})

const emit = defineEmits(['close'])
const props = defineProps({
  activity: {
    type: Object as () => Activity,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
    required: true,
  },
  userId: {
    type: Number,
    required: true,
  },
})

const activityStore = useActivitiesStore()
const { loading } = storeToRefs(activityStore)
const partnerStore = usePartnerStore()
const partnerActivity = ref<Partner | undefined>(undefined)

const $toast = useToast()

const editableActivity = ref<Activity>({ ...props.activity })

const errors = ref<Record<string, string>>({})

const requiredFields = [
  { field: 'summary', label: 'objet' },
  { field: 'dateDeadline', label: 'date' },
]

const onSubmit = async () => {
  const updatedActivityDto: UpdateActivityDto = {
    summary: editableActivity.value.summary,
    note: values.note,
    dateDeadline: editableActivity.value.dateDeadline,
  }
  errors.value = {}
  requiredFields.forEach(({ field, label }) => {
    if (!updatedActivityDto[field as keyof typeof updatedActivityDto]) {
      errors.value[field] = `Le champ ${label} est requis.`
    }
  })

  if (Object.keys(errors.value).length > 0) {
    return
  }

  await activityStore
    .updateActivity(props.activity.id, updatedActivityDto)
    .then(async () => {
      await activityStore.getActivitiesByUserId(props.userId).then(() => {
        $toast.success('To Do mis à jour avec succès.')
        emit('close')
      })
    })
    .catch((error) => {
      $toast.error(error.message)
    })
}

const deleteActivity = async () => {
  await activityStore
    .deleteActivity(props.activity.id)
    .then(async () => {
      await activityStore.getActivitiesByUserId(props.userId).then(() => {
        $toast.success('To Do terminé')
        emit('close')
      })
    })
    .catch((error) => {
      $toast.error(error.message)
    })
}

onMounted(async () => {
  partnerActivity.value = await partnerStore.getPartnerById(props.activity.resId)
})
</script>

<template>
  <Modal :showModal="showModal" @close="emit('close')" title="Détails" :loading="loading">
    <template #content>
      <div class="flex gap-2">
        <PartnerButtonRedirect :partner-id="activity.resId" :partner-name="activity.resName" />
        <PartnerAction v-if="partnerActivity" :partner="partnerActivity" />
      </div>
      <form @submit.prevent="" class="space-y-4">
        <div class="flex gap-2">
          <div class="flex gap-2 w-2/3">
            <div>
              <h3 class="text-lg mt-3">Objet</h3>
              <div>
                <div class="flex flex-col gap-2 w-1/2">
                  <TextInput
                    class="mb-4"
                    v-model="editableActivity!.summary"
                    required
                    :errorMessage="errors.name"
                  />
                </div>
              </div>
              <VQuillEditor
                label="Note"
                name="note"
                v-model="editableActivity!.note"
                placeholder="Ajouter une note"
              ></VQuillEditor>
            </div>
          </div>
          <div>
            <div>
              <h3 class="text-lg">Date</h3>
              <div class="border rounded-xl">
                <DatePicker
                  v-model="editableActivity!.dateDeadline"
                  label="Date"
                  required
                  timezone="Europe/Paris"
                  :attributes="[
                    {
                      highlight: {
                        color: 'darkgrey',
                        fillMode: 'light',
                      },
                      dates: new Date(),
                    },
                  ]"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end gap-4">
          <DeleteConfirmation
            @delete="deleteActivity"
            delete-text="Supprimer"
            confirm-text="Supprimer le To Do"
            cancel-text="Annuler"
          >
            <ButtonDaisy text="Enregistrer" :loader="loading" @click="onSubmit" variant="primary" />
          </DeleteConfirmation>
        </div>
      </form>
    </template>
  </Modal>
</template>
